import { ChangeEvent, useState } from 'react';
import { SEARCH_PICKUP_CONDITION_OPTIONS } from 'constants/searchCondition';
import {
  HolidayAndWayOfWorkingListType,
  RequirementListType,
} from 'types/kodawari';
import { PickUpConditionListType } from 'types/pickUpConditions';
import { changeOrderPickUpRequirement } from 'logics/changeOrderPickUpRequirement/changeOrderPickUpRequirement';

interface ArgumentType {
  initialIsNewArrival: PickUpConditionListType;
  initialIsNearDeadLine: PickUpConditionListType;
  holidayAndWayOfWorkingList: HolidayAndWayOfWorkingListType[];
  requirementList: RequirementListType[];
}

/**
 * こだわり条件ピックアップに関するhooks
 * @param {object} param こだわり条件ピックアップの生成用の引数
 * @param param.holidayAndWayOfWorkingList 休日・働き方の配列
 * @param param.requirementList 募集・採用情報の配列
 * @returns こだわり条件ピックアップの値と更新する関数を含むオブジェクト
 */
export const usePickUpCondition = ({
  initialIsNewArrival,
  initialIsNearDeadLine,
  holidayAndWayOfWorkingList,
  requirementList,
}: ArgumentType) => {
  const [newArrivalData, setNewArrivalData] =
    useState<PickUpConditionListType>(initialIsNewArrival);
  const [nearDeadlineData, setNearDeadlineData] =
    useState<PickUpConditionListType>(initialIsNearDeadLine);

  const pickUpHolidayAndWayOfWorkingList: PickUpConditionListType[] =
    holidayAndWayOfWorkingList
      .filter((item) =>
        SEARCH_PICKUP_CONDITION_OPTIONS.list.some(({ id }) => id === item.id)
      )
      .map((item) => ({
        ...item,
        type: 'holidayAndWayOfWorking',
      }));

  const pickUpRequirementList: PickUpConditionListType[] = requirementList
    .filter((item) =>
      SEARCH_PICKUP_CONDITION_OPTIONS.list.some(({ id }) => id === item.id)
    )
    .map((item) => ({
      ...item,
      type: 'requirement',
    }));

  const pickUpConditionDisplayList: PickUpConditionListType[] = [
    newArrivalData,
    nearDeadlineData,
    ...pickUpHolidayAndWayOfWorkingList,
    ...changeOrderPickUpRequirement(pickUpRequirementList),
  ];

  const isNewArrival = newArrivalData.isChecked;
  const isNearDeadline = nearDeadlineData.isChecked;

  // 新着のチェック状態更新処理
  const updateIsCheckedIsNewArrival = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const checkedObject = {
      ...newArrivalData,
      isChecked: event.target.checked,
    };

    setNewArrivalData(checkedObject);
  };

  // 締切間近のチェック状態更新処理
  const updateIsCheckedIsNearDeadline = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const checkedObject = {
      ...nearDeadlineData,
      isChecked: event.target.checked,
    };

    setNearDeadlineData(checkedObject);
  };

  // 新着と締切間近のチェック状態クリア処理
  const clearIsCheckedIsNewArrivalAndIsNearDeadline = () => {
    const checkedNewArrivalObject = {
      ...newArrivalData,
      isChecked: false,
    };
    const checkedDeadlineObject = {
      ...nearDeadlineData,
      isChecked: false,
    };
    setNewArrivalData(checkedNewArrivalObject);
    setNearDeadlineData(checkedDeadlineObject);
  };

  return {
    isNewArrival,
    isNearDeadline,
    pickUpConditionDisplayList,
    updateIsCheckedIsNewArrival,
    updateIsCheckedIsNearDeadline,
    clearIsCheckedIsNewArrivalAndIsNearDeadline,
  };
};
